import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import clsx from "clsx";
import { FieldArray, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import {
  useStatus,
  usePriorities,
  useSeverity,
  addIncident,
  populateMasterData,
  Attribute,
} from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import Details from "components/incident/AddEdit/Details";
import Location from "components/incident/AddEdit/Location";
import Members from "components/incident/AddEdit/Members";
import IncidentProperties from "components/incident/AddEdit/Properties";
import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";

const Add: React.FC = () => {
  const { t } = useTranslation("incidentAddEdit");
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);

  const sections = [
    { label: t("details.title"), value: "details" },
    { label: t("location.title"), value: "location" },
    { label: t("members.section"), value: "members" },
    {
      label: t("properties.title"),
      value: "properties",
    },
  ];

  const validationSchema = useMemo(
    () =>
      yup.object().shape(
        {
          name: yup.string().trim().required(t("validation.required")),
          type: yup.number().required(t("validation.required")),
          status: yup.number().required(t("validation.required")),
          priority: yup.number().required(t("validation.required")),
          severity: yup.number().required(t("validation.required")),
          address: yup.string(),
          latitude: yup
            .number()
            .typeError(t("validation.latitudeOutOfRange"))
            .min(-90, t("validation.latitudeOutOfRange"))
            .max(90, t("validation.latitudeOutOfRange"))
            .when(["longitude", "findCameras"], {
              is: (longitude: string, findCameras: boolean) =>
                findCameras || longitude != null,
              then: yup
                .number()
                .typeError(t("validation.latitudeOutOfRange"))
                .required(t("validation.required")),
            }),
          longitude: yup
            .number()
            .typeError(t("validation.longitudeOutOfRange"))
            .min(-180, t("validation.longitudeOutOfRange"))
            .max(180, t("validation.longitudeOutOfRange"))
            .when(["latitude", "findCameras"], {
              is: (latitude: string, findCameras: boolean) =>
                findCameras || latitude != null,
              then: yup
                .number()
                .typeError(t("validation.longitudeOutOfRange"))
                .required(t("validation.required")),
            }),
          findCameras: yup.boolean(),
          radius: yup.number().when("findCameras", {
            is: true,
            then: yup
              .number()
              .typeError(t("validation.radiusNotPositiveNumber"))
              .positive(t("validation.radiusNotPositiveNumber"))
              .required(t("validation.required")),
          }),
          attributes: yup.array().of(
            yup.object().shape({
              id: yup.string(),
              label: yup.string().required(t("validation.required")),
              type: yup.string().nullable(),
              value: yup
                .mixed()
                .when("type", {
                  is: "boolean",
                  then: yup.boolean(),
                })
                .when("type", {
                  is: "numeric",
                  then: yup.number().typeError(t("validation.valueNotANumber")),
                })
                .when("type", {
                  is: "hyperlink",
                  then: yup.string().url(t("validation.iconUrlNotValid")),
                }),
            })
          ),
        },
        [["latitude", "longitude"]]
      ),
    [t]
  );

  const onSave = (data: IncidentFormData) => {
    const lat = Number.parseFloat(data.latitude as unknown as string);
    const lng = Number.parseFloat(data.longitude as unknown as string);
    const radius = Number.parseFloat(data.radius as unknown as string);

    const hasLocation = !Number.isNaN(lat) && !Number.isNaN(lng);
    const attributes: Record<string, Attribute> = {};
    if (data.attributes && data.attributes.length > 0)
      data.attributes?.forEach((attr: Attribute) => {
        // exclude id from the object sent to the server
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: attrId, type, value, ...others } = attr;
        let formattedFinalDate;
        if (type === "date and time") {
          formattedFinalDate = value;
        }
        attributes[`${attr.id}`] = {
          ...others,
          type,
          value:
            value && type === "numeric"
              ? Number(value)
              : type === "date and time"
              ? formattedFinalDate
              : value,
          contentType: attr.contentType ? attr.contentType : undefined,
        };
      });

    return addIncident(data.type, {
      inc_name: data.name?.trim(),
      fk_inc_status_id: data.status,
      fk_inc_priority_id: data.priority,
      fk_inc_severity_id: data.severity,
      fk_inc_type_id: data.type,
      inc_assigned_user: data.assignedUser as User,
      inc_watchlist: data.members as User[],
      inc_desc: data.description?.trim(),
      attributes,
      ...(hasLocation
        ? {
            inc_address: data.address,
            location: {
              type: "Point",
              coordinates: [lng, lat],
            },
            nearest_cameras_dist_in_miles: data.findCameras ? radius : 0,
          }
        : {}),
    });
  };

  const onSubmitHandler = async (
    values: IncidentFormData,
    actions: FormikHelpers<IncidentFormData>
  ) => {
    try {
      await onSave(values);
      enqueueSnackbar(t("validation.success.add"), { variant: "success" });
      setPreventNavigation(false);
      history.push("/settings/incident");
      actions.setStatus({});
    } catch (error) {
      setShowBanner(true);
    }
  };

  const { data: statusData, mutate: revalidateStatus } = useStatus({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: priorityData, mutate: revalidatePriority } = usePriorities({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: severityData, mutate: revalidateSeverity } = useSeverity({
    revalidateOnFocus: false,
    suspense: true,
  });

  const calledPopulateMasterData = useRef(false);

  const noStaticData =
    statusData?.data?.length === 0 &&
    priorityData?.data?.length === 0 &&
    severityData?.data?.length === 0;

  useEffect(() => {
    if (noStaticData && !calledPopulateMasterData.current) {
      calledPopulateMasterData.current = true;

      (async () => {
        await populateMasterData();

        await Promise.all([
          revalidateStatus(),
          revalidatePriority(),
          revalidateSeverity(),
        ]);
      })();
    }
  }, [noStaticData, revalidatePriority, revalidateSeverity, revalidateStatus]);

  const initialValues = useMemo((): Partial<IncidentFormData> => {
    return {
      name: "",
      description: "",
      address: "",
      status: statusData?.data.find((v) => v.status_is_default)?.pk_status_id,
      priority: priorityData?.data.find((v) => v.prt_is_default)?.pk_prt_id,
      severity: severityData?.data.find((v) => v.svrt_is_default)?.pk_svrt_id,
      attributes: [],
    };
  }, [priorityData?.data, severityData?.data, statusData?.data]);

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues as IncidentFormData}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submiting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={t("validation.unknownError.add")}
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={t("title")}
                sections={sections}
                onCancel={goBackHandler}
              />
              <HvGrid container spacing={0}>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <Details />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <Location />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[2].value}
                  xs={12}
                  item
                >
                  <Members />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[3].value}
                  xs={12}
                  item
                >
                  <FieldArray
                    name="attributes"
                    render={(arrayHelpers) => (
                      <IncidentProperties {...arrayHelpers} />
                    )}
                  />
                </HvGrid>
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const IncidentAddAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(
      ModuleNames.incidentManagement,
      ModulePolicyTypes.incident
    );
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <Add />{" "}
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default Add;
